/**
 * Перегляд форм фінансової звітності за період без документа (ч.1386): Ф1/Ф2, 1-м/2-м,
 * 1-мс/2-мс, Ф3, Ф4 у гривнях з копійками + попередження розрахунку. Документ для подання
 * до ДПС — /reports/financial (список) → «+ Створити за період».
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { financialReports, type FinReportFormCode, type FinReportLine } from '@/api/financialReports';
import { EQUITY_COLUMNS } from '@/api/financialStatements';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import { pickDefaultOrg } from '@/store/defaults';

const FORM_LABELS: Record<FinReportFormCode, string> = {
  '1ms': '№1-мс — Баланс (мікропідприємство)',
  '2ms': '№2-мс — Звіт про фінрезультати (мікропідприємство)',
  '1m': '№1-м — Баланс (мале підприємство)',
  '2m': '№2-м — Звіт про фінрезультати (мале підприємство)',
  '1': 'Ф1 — Баланс',
  '2': 'Ф2 — Звіт про фінрезультати',
  '3': 'Ф3 — Рух грошових коштів',
  '4': 'Ф4 — Власний капітал',
};
const BALANCE_FORMS = new Set<FinReportFormCode>(['1', '1m', '1ms', '4']);

const fmt = (v: string | number) => {
  const n = Number(v);
  if (n === 0) return '—';
  return n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Спец-значення для селекту: "всі юрособи tenant'а" (наскрізний P&L).
const TENANT_SCOPE = '__tenant__';

export default function FinancialReportsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const [form, setForm] = useState<FinReportFormCode>('1ms');
  const today = new Date();
  const [periodStart, setPeriodStart] = useState(
    new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10),
  );
  const [periodEnd, setPeriodEnd] = useState(
    today.toISOString().slice(0, 10),
  );

  // Баланс і власний капітал — лише по одній юрособі; обороти можна зводити по всьому бізнесу.
  const allowTenantScope = !BALANCE_FORMS.has(form);

  useEffect(() => {
    if (!orgId && orgList.data?.items[0]) {
      const o = pickDefaultOrg(orgList.data.items)!;
      setOrgId(o.id);
      const set = (o as { fin_report_form?: string }).fin_report_form;
      setForm(set === 'FULL' ? '1' : set === 'SMALL' ? '1m' : '1ms');
    }
  }, [orgList.data, orgId]);

  useEffect(() => {
    if (!allowTenantScope && orgId === TENANT_SCOPE && orgList.data?.items[0]) {
      setOrgId(pickDefaultOrg(orgList.data.items)!.id);
    }
  }, [allowTenantScope, orgId, orgList.data]);

  const reportQ = useQuery({
    queryKey: ['fin-report', form, orgId, periodStart, periodEnd],
    queryFn: () => financialReports.get({
      form_code: form,
      period_start: periodStart,
      period_end: periodEnd,
      ...(orgId === TENANT_SCOPE ? {} : { organization_id: orgId }),
    }),
    enabled: !!orgId,
  });

  const linesBySection: Array<{ section: string; lines: FinReportLine[] }> = [];
  for (const line of reportQ.data?.lines ?? []) {
    const sec = line.section || '';
    const last = linesBySection[linesBySection.length - 1];
    if (last && last.section === sec) last.lines.push(line);
    else linesBySection.push({ section: sec, lines: [line] });
  }
  const isBalance = BALANCE_FORMS.has(form);
  const colStart = isBalance ? 'На початок періоду' : 'За той самий період попереднього року';
  const colEnd = isBalance ? 'На кінець періоду' : 'За період';

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Фінансова звітність — перегляд за період</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <button onClick={() => navigate('/reports/financial')}>до документів</button>
          <button onClick={() => window.print()}>друкувати</button>
        </div>
      </div>

      <div className="doc-grid" style={{ marginBottom: '1rem' }}>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Форма</label>
          <select value={form} onChange={(e) => setForm(e.target.value as FinReportFormCode)}>
            {(Object.keys(FORM_LABELS) as FinReportFormCode[]).map((k) =>
              <option key={k} value={k}>{FORM_LABELS[k]}</option>,
            )}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {allowTenantScope && (
              <option value={TENANT_SCOPE}>Весь бізнес (усі юрособи)</option>
            )}
            {orgList.data?.items.map((o) =>
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>,
            )}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період з</label>
          <DatePopover value={periodStart} onChange={(v) => setPeriodStart(v)} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період по</label>
          <DatePopover value={periodEnd} onChange={(v) => setPeriodEnd(v)} />
        </div>
      </div>

      {reportQ.isLoading && (
        <p style={{ color: 'var(--muted)' }}>Розрахунок…</p>
      )}

      {reportQ.data && (
        <>
          <h3 style={{ marginTop: 0 }}>{reportQ.data.form_name}</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            {reportQ.data.scope === 'tenant'
              ? `По всьому бізнесу (${reportQ.data.organization_ids.length} юр.осіб) · `
              : reportQ.data.scope === 'organizations'
                ? `По обраних юрособах (${reportQ.data.organization_ids.length}) · `
                : ''}
            Період: {new Date(reportQ.data.period_start).toLocaleDateString('uk-UA')}
            {' — '}
            {new Date(reportQ.data.period_end).toLocaleDateString('uk-UA')} · суми у гривнях з копійками
          </p>
          {reportQ.data.warnings.length > 0 && (
            <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem' }}>
              <b>Перевірте:</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{reportQ.data.warnings.map((w, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{w}</li>)}</ul>
            </div>
          )}

          {form === '4' ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="tch" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>Код</th>
                    <th>Стаття</th>
                    {EQUITY_COLUMNS.map((c) => <th key={c.col} style={{ textAlign: 'right', minWidth: 110 }}>{c.title}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(reportQ.data.lines ?? []).map((l) => (
                    <tr key={l.line_code} style={{ fontWeight: l.is_total ? 700 : 400, background: l.is_total ? 'var(--primary-soft)' : 'transparent' }}>
                      <td style={{ fontFamily: 'monospace' }}>{l.line_code}</td>
                      <td style={{ paddingLeft: l.parent_line ? '1.5rem' : undefined }}>{l.line_name}</td>
                      {EQUITY_COLUMNS.map((c) => (
                        <td key={c.col} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.cells?.[c.col] ?? 0)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : linesBySection.map((sect, si) => (
            <div key={si} style={{ marginTop: '1rem' }}>
              {sect.section && (
                <h4 style={{
                  margin: '0 0 0.3rem', fontSize: '0.9rem',
                  color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)',
                  paddingBottom: '0.3rem',
                }}>
                  {sect.section}
                </h4>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-soft-2)' }}>
                    <th style={{ padding: '0.4rem', width: 70, textAlign: 'left' }}>Код</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Стаття</th>
                    <th style={{ padding: '0.4rem', width: 190, textAlign: 'right' }}>{colStart}</th>
                    <th style={{ padding: '0.4rem', width: 190, textAlign: 'right' }}>{colEnd}</th>
                  </tr>
                </thead>
                <tbody>
                  {sect.lines.map((l) => (
                    <tr key={l.line_code} style={{
                      borderTop: '1px solid var(--border-soft)',
                      background: l.is_total ? 'var(--primary-soft)' : 'transparent',
                      fontWeight: l.is_total ? 700 : 400,
                    }}>
                      <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>
                        {l.line_code}
                      </td>
                      <td style={{
                        padding: '0.4rem',
                        paddingLeft: l.parent_line ? '1.5rem' : '0.4rem',
                        color: l.is_negative ? 'var(--danger)' : 'inherit',
                      }} title={l.is_manual ? 'Рядок без формули — заповнюється вручну у документі' : undefined}>
                        {l.is_negative ? '(' : ''}{l.line_name}{l.is_negative ? ')' : ''}
                        {l.is_manual ? <span style={{ color: 'var(--muted)' }}> (вручну)</span> : null}
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(l.column_start)}
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(l.column_end)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}

      <style>{`@media print { .app__header, .app__sidebar, .toolbar button { display: none !important; } }`}</style>
    </div>
  );
}
